<template>
    <SettingsPage
        :schema="schema"
        :initial-value="currentSettings"
        :loading="loading"
        :updating="updating"
        :validate-support-time="validateSupportTime"
        title="Support Contact Information"
        :description="description"
        @save="save"
    >
        <template #preview="{ value }">
            <h3>Email Preview</h3>

            <ActionEmailPreview :value="value" :defaults="currentSettings" />

            <div class="SettingsPage__note">
                Note: The text displayed in the email above is for preview purposes only.
                The actual text used in your email is controlled on a per-template basis.
            </div>
        </template>

        <template #default="{ fields }">
            <div class="SettingsPage__formRow">
                <JsonSchemaCustomFormElement :field="fields.supportUrl" />
                <JsonSchemaCustomFormElement :field="fields.supportEmail" />
            </div>

            <div class="SettingsPage__formRow">
                <JsonSchemaCustomFormElement :field="fields.supportUrlTitle" />
                <JsonSchemaCustomFormElement :field="fields.supportPhone" />
            </div>

            <hr>

            <h3>Support Availability</h3>

            <div class="SettingsPage__formRow">
                <div class="SettingsPage__formRowDescription">
                    Select days of the week
                </div>
                <div>
                    <FormElement>
                        <FormInput>
                            <DayOfWeekInput
                                v-model="fields.supportDaysOfWeek.value"
                                :disabled="fields.supportDaysOfWeek.disabled"
                                @update:model-value="fields.supportDaysOfWeek.touch()"
                            />
                        </FormInput>

                        <InputZoneSelector show error>
                            <JsonSchemaCustomFormElement :field="fields.supportDaysOfWeek" />
                        </InputZoneSelector>
                    </FormElement>
                </div>
            </div>

            <template v-if="fields.supportDaysOfWeek.value.length > 0">
                <div class="SettingsPage__formRow">
                    <div class="SettingsPage__formRowDescription">
                        Select time range
                    </div>
                    <InputZoneSelector hide label clear-button>
                        <JsonSchemaCustomFormElement
                            :field="fields.supportStartTime"
                        />
                    </InputZoneSelector>
                    <span class="SettingsPage__formRowSeparator" />
                    <InputZoneSelector hide label clear-button>
                        <JsonSchemaCustomFormElement
                            :field="fields.supportEndTime"
                        />
                    </InputZoneSelector>
                </div>

                <div class="SettingsPage__formRow">
                    <div class="SettingsPage__formRowDescription">
                        Select time zone
                    </div>
                    <InputZoneSelector hide label clear-button>
                        <JsonSchemaCustomFormElement :field="fields.supportTimezone" />
                    </InputZoneSelector>
                </div>
            </template>
        </template>
    </SettingsPage>
</template>

<script lang="ts">
    import { Component, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { DayOfWeekInput } from '@evidentid/dashboard-commons/components/DayOfWeekInput';
    import { FormInput, FormElement } from '@evidentid/dashboard-commons/components/Form';
    import { JsonSchemaCustomFormElement } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import { useNotificationsConfigurationStore } from '@/modules/notifications-configuration/store';
    import { usePageLayoutStore } from '@/modules/page-layout/store';

    import InputZoneSelector from '../../notifications-configuration/components/InputZoneSelector.vue';
    import ActionEmailPreview from '../components/ActionEmailPreview.vue';
    import SettingsPage from '../components/SettingsPage.vue';
    import { buildSupportContactDetailsSchema } from '../schemas/buildSupportContactDetailsSchema';
    import {
        SupportContactConfiguration,
        NotificationsConfiguration,
        NotificationsConfigurationState,
    } from '../types';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';

    @Component({
        components: {
            SettingsPage,
            ActionEmailPreview,
            JsonSchemaCustomFormElement,
            DayOfWeekInput,
            InputZoneSelector,
            FormElement,
            FormInput,
        },
    })
    export default class SupportContactInformation extends Vue {
        private get notificationsConfigurationStore() {
            return useNotificationsConfigurationStore();
        }

        private get pageLayoutStore() {
            return usePageLayoutStore();
        }

        private schema = buildSupportContactDetailsSchema();
        private description = 'Define the support details shown to entities, including contact links, email addresses, phone numbers, and support hours.';

        private created(): void {
            this.pageLayoutStore.resetPageLayout();
        }

        private get rpName(): string {
            return this.$route.params.rpId as string;
        }

        private get state(): NotificationsConfigurationState {
            return this.notificationsConfigurationStore.$state;
        }

        private get status(): OperationStatus {
            return this.state.loadSettingsStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get loading(): boolean {
            return ![ OperationStatus.success, OperationStatus.error ].includes(this.status);
        }

        private get updateStatus(): OperationStatus {
            return this.state.updateSettingsStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get updating(): boolean {
            return this.updateStatus === OperationStatus.loading;
        }

        private get currentSettings(): NotificationsConfiguration | null {
            return this.state.settings[this.rpName] || null;
        }

        @Watch('loading', { immediate: true })
        private onLoadingChange(): void {
            this.pageLayoutStore.setLoading(this.loading);
        }

        @Watch('rpName', { immediate: true })
        private async onRpChange(newVal: string, oldVal: string): Promise<void> {
            if (newVal && newVal !== oldVal) {
                await useRbacStore().validateRbac([ RbacFeature.displayAndEmailContactInformation ]);
            }
        }

        private async save(settings: SupportContactConfiguration): Promise<void> {
            await this.notificationsConfigurationStore.updateSettings({
                rpName: this.rpName,
                settings,
            });

            if (this.updateStatus === OperationStatus.success) {
                useSnackbarStore().displaySnackbar({
                    message: 'Contact information updated successfully.',
                    success: true,
                });
            }
        }

        @Watch('status', { immediate: true })
        private loadSettings(): void {
            if (this.status === OperationStatus.uninitialized) {
                this.notificationsConfigurationStore.loadSettings({ rpName: this.rpName });
            }
        }

        private validateSupportTime(value: SupportContactConfiguration): boolean {
            const { supportDaysOfWeek, supportEndTime, supportStartTime } = value || {};

            if (!supportDaysOfWeek?.length || !supportEndTime || !supportStartTime) {
                return true;
            }

            const [ endTime24Hours, endTimeMinutes ] = supportEndTime.split(':').map((time) => parseInt(time, 10));
            const [ startTime24Hours, startTimeMinutes ] = supportStartTime.split(':').map((time) => parseInt(time, 10));

            if (startTime24Hours > endTime24Hours) {
                return false;
            }

            return !(startTime24Hours === endTime24Hours && startTimeMinutes >= endTimeMinutes);
        }
    }
</script>
